import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { slaPorCliente } from "../../../utils/centralDeAlertas/correlacao";
import { buscarKpis } from "./apiKpis";
import { buscarAlertasPeriodo } from "./apiAlertasPeriodo";
import { buscarIncidentesPeriodo } from "./apiIncidentes";
import { buscarFiltrosDashboard } from "./apiFiltros";
import useErpSelecionado from "../../erp/useErpSelecionado";
import { getErpPrincipal } from "../../../services/erpSistema";

dayjs.locale("pt-br");

// Estado global da Central de Alertas: filtros (com debounce de 200ms),
// incidentes correlacionados, eventos globais, agregados de gráficos, foco
// de incidente (TimelineGlobal -> TabelaIncidentes) e toast.
//
// Todos os dados vêm do backend real:
// - `kpis` (8 cards) — apiKpis.js, flag `carregandoKpis`;
// - `alertasPorDia`/`rankingEquipamentos` — apiAlertasPeriodo.js, flag
//   `carregandoAlertasPeriodo`;
// - `incidentes`/`eventos` (tabela de Incidentes + Linha do tempo global +
//   Alertas inteligentes) — apiIncidentes.js, flag `carregando`;
// - opções de cliente/equipamento dos filtros — apiFiltros.js, flag
//   `carregandoFiltros`. Como os ids são reais (Clientes.id / hostId Zabbix),
//   filterClientes/filterEquipamentos são enviados a TODAS as rotas.
//
// O período segue o padrão das demais páginas (ButtonTime): initialDate/
// finalDate resolvidos + selectTime (índice do preset relativo, null =
// intervalo absoluto).
//
// ERP: com mais de um ERP cadastrado, o painel mostra um de cada vez
// (`filtros.erpId`, escolhido no FiltrosDashboard) — só os hosts dos clientes
// dele, com os atendimentos/OS dele. Vai como `erpId` em todas as rotas; sem
// ERP cadastrado fica null e o backend segue a variável ERP (como antes).
// `undefined` = lista de ERPs ainda não carregada (nenhuma busca sai).

const CentralAlertasContext = createContext(null);

const DEBOUNCE_FILTROS_MS = 200;
// Índice do preset relativo padrão ("Últimos 7 dias") na tabela de presets
// canônica do ButtonTime — exportado para o FiltrosDashboard.
export const PRESET_PADRAO = 9;

// Factory (não constante) para as datas nascerem no momento da montagem.
function criarFiltrosIniciais() {
	const agora = dayjs().set("second", 0).set("millisecond", 0);
	return {
		selectTime: PRESET_PADRAO,
		initialDate: agora.subtract(7, "day").toDate(),
		finalDate: agora.toDate(),
		clientes: [],
		equipamentos: [],
		severidades: [],
		status: [],
	};
}

export function CentralAlertasProvider({ children }) {
	// Referência de tempo fixa na montagem para rótulos de dia estáveis.
	const agora = useMemo(() => dayjs(), []);

	const [filtros, setFiltros] = useState(criarFiltrosIniciais);
	const [filtrosAplicados, setFiltrosAplicados] = useState(filtros);
	const [focoIncidenteId, setFocoIncidenteId] = useState(null);
	const [toast, setToast] = useState({ aberto: false, mensagem: "" });
	const [kpis, setKpis] = useState({ atual: {}, anterior: {} });
	const [carregandoKpis, setCarregandoKpis] = useState(true);
	const [alertasPorDia, setAlertasPorDia] = useState([]);
	const [rankingEquipamentos, setRankingEquipamentos] = useState([]);
	const [carregandoAlertasPeriodo, setCarregandoAlertasPeriodo] = useState(true);
	const [incidentes, setIncidentes] = useState([]);
	const [eventos, setEventos] = useState([]);
	// Alertas tirados do painel pela configuração de exibição (tela /config).
	const [alertasOcultos, setAlertasOcultos] = useState(0);
	// Janela de correlação em vigor no backend (aba Parâmetros da /config);
	// null enquanto não carregou — os consumidores usam fallback de 30.
	const [janelaCorrelacaoMinutos, setJanelaCorrelacaoMinutos] = useState(null);
	const [janelaAgrupamentoMinutos, setJanelaAgrupamentoMinutos] = useState(null);
	const [carregandoIncidentes, setCarregandoIncidentes] = useState(true);
	const [clientes, setClientes] = useState([]);
	const [equipamentos, setEquipamentos] = useState([]);
	const [carregandoFiltros, setCarregandoFiltros] = useState(true);
	const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado(null, true);

	// ERP escolhido entra nos filtros (mesmo debounce), limpando cliente/
	// equipamento — as opções eram do ERP anterior.
	useEffect(() => {
		if (!carregado) return;
		setFiltros((atuais) =>
			atuais.erpId === erpId
				? atuais
				: { ...atuais, erpId, clientes: [], equipamentos: [] },
		);
	}, [carregado, erpId]);

	useEffect(() => {
		const timer = setTimeout(
			() => setFiltrosAplicados(filtros),
			DEBOUNCE_FILTROS_MS,
		);
		return () => clearTimeout(timer);
	}, [filtros]);

	const atualizarFiltros = useCallback((parcial) => {
		setFiltros((atuais) => ({ ...atuais, ...parcial }));
	}, []);

	// O ERP escolhido não é "limpo" (seleção obrigatória).
	const limparFiltros = useCallback(() => {
		setFiltros((atuais) => ({ ...criarFiltrosIniciais(), erpId: atuais.erpId }));
	}, []);

	// Clique no TopEquipamentos: alterna o filtro para um único equipamento.
	const aplicarFiltroEquipamento = useCallback((equipamentoId) => {
		setFiltros((atuais) => {
			const jaFiltrado =
				atuais.equipamentos.length === 1 &&
				atuais.equipamentos[0] === equipamentoId;
			return { ...atuais, equipamentos: jaFiltrado ? [] : [equipamentoId] };
		});
	}, []);

	const focarIncidente = useCallback((incidenteId) => {
		setFocoIncidenteId(incidenteId);
	}, []);

	const limparFoco = useCallback(() => setFocoIncidenteId(null), []);

	// Atualização OTIMISTA pós-reconhecimento: marca os alertas como
	// reconhecidos direto no estado (alerta.reconhecido + status derivado),
	// para o accordion/tabela refletirem na hora — o refetch demoraria e o
	// backend invalida o próprio cache na ação. Espelha derivarStatusIncidente
	// do backend: o reconhecimento só muda status quando ele era "aberto".
	const marcarAlertasReconhecidos = useCallback((eventIds) => {
		const ids = new Set((eventIds || []).filter(Boolean).map(String));
		if (!ids.size) return;
		setIncidentes((atuais) =>
			(atuais || []).map((incidente) => {
				if (!incidente?.alerta || !ids.has(String(incidente.alerta.id))) {
					return incidente;
				}
				return {
					...incidente,
					status: incidente.status === "aberto" ? "reconhecido" : incidente.status,
					alerta: { ...incidente.alerta, reconhecido: true },
				};
			})
		);
	}, []);

	const mostrarToast = useCallback((mensagem) => {
		setToast({ aberto: true, mensagem });
	}, []);

	const fecharToast = useCallback(() => {
		setToast((atual) => ({ ...atual, aberto: false }));
	}, []);

	// ERP dos dados em tela (o dos filtros aplicados) e o tipo dele — decide
	// as ações disponíveis (criar atendimento/OS, detalhe do ERP).
	const erpAplicado = filtrosAplicados.erpId;
	const tipoErp =
		erps?.find((erp) => erp.id === erpAplicado)?.tipo ||
		(erps?.length ? null : getErpPrincipal());

	// Opções reais de cliente/equipamento dos filtros — uma busca por ERP
	// escolhido (o backend cacheia por usuário e ERP).
	useEffect(() => {
		if (erpAplicado === undefined) return undefined;
		const controller = new AbortController();
		setCarregandoFiltros(true);

		buscarFiltrosDashboard({ erpId: erpAplicado, signal: controller.signal })
			.then((resultado) => {
				if (!controller.signal.aborted) {
					setClientes(resultado.clientes);
					setEquipamentos(resultado.equipamentos);
				}
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error(
						"Erro ao buscar filtros da Central de Alertas:",
						error,
					);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregandoFiltros(false);
			});

		return () => controller.abort();
	}, [erpAplicado]);

	const intervalo = useMemo(
		() => ({
			inicio: dayjs(filtrosAplicados.initialDate),
			fim: dayjs(filtrosAplicados.finalDate),
		}),
		[filtrosAplicados.initialDate, filtrosAplicados.finalDate],
	);

	// Deps primitivas dos fetches: os efeitos abaixo dependem destes valores
	// (não das referências de `intervalo`/`filtrosAplicados`) para refazer a
	// chamada apenas quando um filtro que o backend usa realmente muda.
	const inicioMs = intervalo.inicio.valueOf();
	const fimMs = intervalo.fim.valueOf();
	const clientesChave = JSON.stringify(filtrosAplicados.clientes || []);
	const equipamentosChave = JSON.stringify(filtrosAplicados.equipamentos || []);
	const severidadesChave = JSON.stringify(filtrosAplicados.severidades || []);

	// Busca real dos 8 KPIs.
	useEffect(() => {
		if (erpAplicado === undefined) return undefined;
		const controller = new AbortController();
		setCarregandoKpis(true);

		buscarKpis({
			inicio: dayjs(inicioMs),
			fim: dayjs(fimMs),
			filterClientes: JSON.parse(clientesChave),
			filterEquipamentos: JSON.parse(equipamentosChave),
			filterSeveridades: JSON.parse(severidadesChave),
			erpId: erpAplicado,
			signal: controller.signal,
		})
			.then((resultado) => {
				if (!controller.signal.aborted) setKpis(resultado);
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error("Erro ao buscar KPIs da Central de Alertas:", error);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregandoKpis(false);
			});

		return () => controller.abort();
	}, [erpAplicado, inicioMs, fimMs, clientesChave, equipamentosChave, severidadesChave]);

	// Busca real de "Volume diário de alertas" + "Top equipamentos" — mesmas
	// dependências e mesmo padrão de cancelamento do efeito dos KPIs acima.
	useEffect(() => {
		if (erpAplicado === undefined) return undefined;
		const controller = new AbortController();
		setCarregandoAlertasPeriodo(true);

		buscarAlertasPeriodo({
			inicio: dayjs(inicioMs),
			fim: dayjs(fimMs),
			filterClientes: JSON.parse(clientesChave),
			filterEquipamentos: JSON.parse(equipamentosChave),
			filterSeveridades: JSON.parse(severidadesChave),
			erpId: erpAplicado,
			signal: controller.signal,
		})
			.then((resultado) => {
				if (!controller.signal.aborted) {
					setAlertasPorDia(resultado.alertasPorDia);
					setRankingEquipamentos(resultado.rankingEquipamentos);
				}
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error(
						"Erro ao buscar volume diário/top equipamentos da Central de Alertas:",
						error
					);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregandoAlertasPeriodo(false);
			});

		return () => controller.abort();
	}, [erpAplicado, inicioMs, fimMs, clientesChave, equipamentosChave, severidadesChave]);

	// Busca real de "Incidentes" + "Linha do tempo global" (mesmo motor no
	// backend — apiIncidentes.js busca as duas juntas).
	useEffect(() => {
		if (erpAplicado === undefined) return undefined;
		const controller = new AbortController();
		setCarregandoIncidentes(true);

		buscarIncidentesPeriodo({
			inicio: dayjs(inicioMs),
			fim: dayjs(fimMs),
			filterClientes: JSON.parse(clientesChave),
			filterEquipamentos: JSON.parse(equipamentosChave),
			filterSeveridades: JSON.parse(severidadesChave),
			erpId: erpAplicado,
			signal: controller.signal,
		})
			.then((resultado) => {
				if (!controller.signal.aborted) {
					setIncidentes(resultado.incidentes);
					setEventos(resultado.eventos);
					setAlertasOcultos(resultado.ocultos);
					setJanelaCorrelacaoMinutos(resultado.janelaCorrelacaoMinutos);
					setJanelaAgrupamentoMinutos(resultado.janelaAgrupamentoMinutos);
				}
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error(
						"Erro ao buscar incidentes/linha do tempo da Central de Alertas:",
						error
					);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregandoIncidentes(false);
			});

		return () => controller.abort();
	}, [erpAplicado, inicioMs, fimMs, clientesChave, equipamentosChave, severidadesChave]);

	// Período/cliente/equipamento/severidade já vêm filtrados do backend; o
	// único corte local que resta é o status, derivado por incidente.
	const incidentesFiltrados = useMemo(() => {
		if (!filtrosAplicados.status?.length) return incidentes;
		return incidentes.filter((incidente) =>
			filtrosAplicados.status.includes(incidente.status),
		);
	}, [incidentes, filtrosAplicados.status]);

	// "Exibindo X incidentes de Y": Y considera apenas o período (o backend
	// não filtra por status).
	const totalIncidentesPeriodo = incidentes.length;

	// Eventos já chegam filtrados do backend (status não se aplica a eventos,
	// mesma regra do antigo filtrarEventos).
	const eventosFiltrados = eventos;

	const slaClientes = useMemo(
		() => slaPorCliente(incidentesFiltrados, [], clientes),
		[incidentesFiltrados, clientes],
	);

	const equipamentosDisponiveis = useMemo(() => {
		if (!filtros.clientes.length) return equipamentos;
		return equipamentos.filter((equipamento) =>
			filtros.clientes.includes(equipamento.clienteId),
		);
	}, [equipamentos, filtros.clientes]);

	const valor = useMemo(
		() => ({
			agora,
			// ERP: lista, escolhido (select) e o dos dados em tela + tipo dele.
			erps,
			erpId,
			setErpId,
			variosErps,
			erpAplicado,
			tipoErp,
			// `carregando` é a flag dos blocos alimentados por incidentes/eventos
			// (TabelaIncidentes, TimelineGlobal, AlertasInteligentes, SlaPorCliente).
			carregando: carregandoIncidentes,
			carregandoFiltros,
			alertasOcultos,
			janelaCorrelacaoMinutos,
			janelaAgrupamentoMinutos,
			clientes,
			equipamentos,
			equipamentosDisponiveis,
			filtros,
			filtrosAplicados,
			atualizarFiltros,
			limparFiltros,
			aplicarFiltroEquipamento,
			intervalo,
			incidentes,
			incidentesFiltrados,
			incidentesPeriodoAnterior: [],
			totalIncidentesPeriodo,
			eventosFiltrados,
			kpis,
			carregandoKpis,
			alertasPorDia,
			rankingEquipamentos,
			carregandoAlertasPeriodo,
			slaClientes,
			focoIncidenteId,
			focarIncidente,
			limparFoco,
			marcarAlertasReconhecidos,
			toast,
			mostrarToast,
			fecharToast,
		}),
		[
			agora,
			erps,
			erpId,
			setErpId,
			variosErps,
			erpAplicado,
			tipoErp,
			carregandoIncidentes,
			carregandoFiltros,
			alertasOcultos,
			janelaCorrelacaoMinutos,
			janelaAgrupamentoMinutos,
			clientes,
			equipamentos,
			equipamentosDisponiveis,
			filtros,
			filtrosAplicados,
			atualizarFiltros,
			limparFiltros,
			aplicarFiltroEquipamento,
			intervalo,
			incidentes,
			incidentesFiltrados,
			totalIncidentesPeriodo,
			eventosFiltrados,
			kpis,
			carregandoKpis,
			alertasPorDia,
			rankingEquipamentos,
			carregandoAlertasPeriodo,
			slaClientes,
			focoIncidenteId,
			focarIncidente,
			limparFoco,
			marcarAlertasReconhecidos,
			toast,
			mostrarToast,
			fecharToast,
		],
	);

	return (
		<CentralAlertasContext.Provider value={valor}>
			{children}
		</CentralAlertasContext.Provider>
	);
}

export function useCentralAlertas() {
	const contexto = useContext(CentralAlertasContext);
	if (!contexto) {
		throw new Error(
			"useCentralAlertas deve ser usado dentro de CentralAlertasProvider",
		);
	}
	return contexto;
}
